import { useState, useEffect } from "react";
import {
  createCredential,
  updateCredential,
  adminCreateCredential,
  adminUpdateCredential,
  generatePassword,
  adminGeneratePassword,
  getCredentialPassword,
  getPassword as adminGetPassword,
  getCredentialPasswordHistory,
  getPasswordHistory as adminGetPasswordHistory,
  logCredentialAccess,
  adminLogCredentialAccess,
  getCredentialAccessLog,
  adminGetAccessLog,
} from "../../services/credentialService.js";
import TotpWidget from "./TotpWidget.jsx";
import { BRAND_AWARE_TYPES, resolveBrandDomain, faviconSrc } from "../../utils/brandIcons.js";

// ─────────────────────────────────────────────
//  TYPES — alignés sur CredentialType.java
// ─────────────────────────────────────────────

const TYPES = [
  { id: "WEBSITE",     label: "Site Web",        bg: "#3B5BDB" },
  { id: "APPLICATION", label: "Application",     bg: "#6741D9" },
  { id: "DATABASE",    label: "Base de données", bg: "#C2255C" },
  { id: "EMAIL",       label: "E-mail",          bg: "#2F9E44" },
  { id: "SERVER",      label: "Serveur",         bg: "#3D6B6B" },
  { id: "SSH",         label: "SSH",             bg: "#1A1A1A" },
  { id: "VPN",         label: "VPN",             bg: "#0077B6" },
  { id: "WIFI",        label: "WiFi",            bg: "#9B2AE8" },
  { id: "ROUTER",      label: "Routeur",         bg: "#E85D04" },
  { id: "NAS",         label: "NAS",             bg: "#1864AB" },
  { id: "PRINTER",     label: "Imprimante",      bg: "#6B7280" },
  { id: "CLOUD_BACKUP",label: "Sauvegarde Cloud",bg: "#15AABF" },
  { id: "IP_PHONE",    label: "Téléphonie IP",   bg: "#0CA678" },
  { id: "PBX",         label: "PBX",             bg: "#F08C00" },
  { id: "SWITCH",      label: "Switch",          bg: "#495057" },
  { id: "OTHER",       label: "Autre",           bg: "#F76707" },
];

// ─────────────────────────────────────────────
//  TYPES DE CHAMPS PERSONNALISÉS — façon PasswordBoss
//  Chaque champ additionnel a son propre type, qui détermine
//  la façon dont sa valeur est saisie/affichée (masquée pour
//  un mot de passe, sélecteur natif pour une date, etc.)
// ─────────────────────────────────────────────

const CUSTOM_FIELD_TYPES = [
  { id: "TEXT",     label: "Texte" },
  { id: "PASSWORD", label: "Mot de passe" },
  { id: "CHECKBOX", label: "Case à cocher" },
  { id: "URL",      label: "URL" },
  { id: "EMAIL",    label: "E-mail" },
  { id: "DATE",     label: "Date" },
  { id: "NUMBER",   label: "Nombre" },
];

// ─────────────────────────────────────────────
//  ILLUSTRATIONS SVG PAR TYPE (bannière modale)
// ─────────────────────────────────────────────

const SVG = {
  WEBSITE: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <defs><linearGradient id="g1" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#3B5BDB"/><stop offset="100%" stopColor="#4DABF7"/></linearGradient></defs>
      <rect width="160" height="90" fill="url(#g1)"/>
      <circle cx="80" cy="45" r="26" fill="none" stroke="rgba(255,255,255,.9)" strokeWidth="2.5"/>
      <ellipse cx="80" cy="45" rx="11" ry="26" fill="none" stroke="rgba(255,255,255,.7)" strokeWidth="1.8"/>
      <line x1="54" y1="45" x2="106" y2="45" stroke="rgba(255,255,255,.7)" strokeWidth="1.8"/>
    </svg>
  ),
  APPLICATION: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <defs><linearGradient id="g2" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#6741D9"/><stop offset="100%" stopColor="#9775FA"/></linearGradient></defs>
      <rect width="160" height="90" fill="url(#g2)"/>
      <circle cx="80" cy="44" r="16" fill="rgba(255,255,255,.9)"/>
      <polyline points="80,58 80,38" stroke="#6741D9" strokeWidth="3" strokeLinecap="round"/>
      <polyline points="72,46 80,38 88,46" stroke="#6741D9" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  ),
  DATABASE: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <defs><linearGradient id="g3" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#C2255C"/><stop offset="100%" stopColor="#F06595"/></linearGradient></defs>
      <rect width="160" height="90" fill="url(#g3)"/>
      <ellipse cx="80" cy="28" rx="24" ry="8" fill="rgba(255,255,255,.88)"/>
      <rect x="56" y="28" width="48" height="14" fill="rgba(255,255,255,.75)"/>
      <ellipse cx="80" cy="42" rx="24" ry="8" fill="rgba(255,255,255,.88)"/>
      <rect x="56" y="42" width="48" height="14" fill="rgba(255,255,255,.65)"/>
      <ellipse cx="80" cy="56" rx="24" ry="8" fill="rgba(255,255,255,.88)"/>
    </svg>
  ),
  EMAIL: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <defs><linearGradient id="g4" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#2F9E44"/><stop offset="100%" stopColor="#69DB7C"/></linearGradient></defs>
      <rect width="160" height="90" fill="url(#g4)"/>
      <rect x="38" y="26" width="84" height="52" rx="4" fill="rgba(255,255,255,.88)"/>
      <polyline points="38,26 80,56 122,26" fill="none" stroke="rgba(47,158,68,.8)" strokeWidth="2.5"/>
    </svg>
  ),
  SERVER: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <defs><linearGradient id="g5" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#3D6B6B"/><stop offset="100%" stopColor="#63B3B3"/></linearGradient></defs>
      <rect width="160" height="90" fill="url(#g5)"/>
      <rect x="52" y="12" width="56" height="66" rx="5" fill="rgba(255,255,255,.15)" stroke="rgba(255,255,255,.6)" strokeWidth="1.5"/>
      {[18,30,42,54].map((y, i) => <g key={i}><rect x="55" y={y} width="50" height="9" rx="2" fill="rgba(255,255,255,.18)"/><circle cx="98" cy={y+4.5} r="2.5" fill={i<2?"#69DB7C":"#FFD43B"}/></g>)}
    </svg>
  ),
  SSH: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <rect width="160" height="90" fill="#1A1A1A"/>
      <rect x="22" y="16" width="116" height="62" rx="6" fill="#0D1117"/>
      <circle cx="33" cy="24" r="4" fill="#FF5F57"/><circle cx="46" cy="24" r="4" fill="#FFBD2E"/><circle cx="59" cy="24" r="4" fill="#28C840"/>
      <text x="30" y="46" fontFamily="monospace" fontSize="8" fill="#58A6FF">$</text>
      <text x="40" y="46" fontFamily="monospace" fontSize="8" fill="#7EE787">ssh admin@192.168.1.1</text>
    </svg>
  ),
  VPN: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <defs><linearGradient id="g7" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#0077B6"/><stop offset="100%" stopColor="#00B4D8"/></linearGradient></defs>
      <rect width="160" height="90" fill="url(#g7)"/>
      <circle cx="80" cy="45" r="28" fill="none" stroke="rgba(255,255,255,.3)" strokeWidth="1.5"/>
      <circle cx="80" cy="45" r="18" fill="none" stroke="rgba(255,255,255,.6)" strokeWidth="1.5"/>
      <circle cx="80" cy="45" r="8"  fill="rgba(255,255,255,.85)"/>
    </svg>
  ),
  WIFI: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <defs><linearGradient id="g8" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#9B2AE8"/><stop offset="100%" stopColor="#DA77F2"/></linearGradient></defs>
      <rect width="160" height="90" fill="url(#g8)"/>
      <circle cx="80" cy="74" r="6" fill="white"/>
      <path d="M62 60 Q80 46 98 60" fill="none" stroke="rgba(255,255,255,.9)" strokeWidth="4.5" strokeLinecap="round"/>
      <path d="M48 48 Q80 28 112 48" fill="none" stroke="rgba(255,255,255,.7)" strokeWidth="4.5" strokeLinecap="round"/>
      <path d="M34 36 Q80 10 126 36" fill="none" stroke="rgba(255,255,255,.45)" strokeWidth="4.5" strokeLinecap="round"/>
    </svg>
  ),
  ROUTER: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <defs><linearGradient id="g9" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#E85D04"/><stop offset="100%" stopColor="#F48C06"/></linearGradient></defs>
      <rect width="160" height="90" fill="url(#g9)"/>
      <rect x="40" y="44" width="80" height="20" rx="5" fill="rgba(255,255,255,.85)"/>
      {[56,68,80,92,104].map(x => <circle key={x} cx={x} cy="54" r="3" fill="#E85D04"/>)}
      <line x1="60" y1="44" x2="50" y2="28" stroke="rgba(255,255,255,.8)" strokeWidth="2"/>
      <line x1="100" y1="44" x2="110" y2="28" stroke="rgba(255,255,255,.8)" strokeWidth="2"/>
    </svg>
  ),
  NAS: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <defs><linearGradient id="g13" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#1864AB"/><stop offset="100%" stopColor="#4DABF7"/></linearGradient></defs>
      <rect width="160" height="90" fill="url(#g13)"/>
      <rect x="54" y="14" width="52" height="62" rx="5" fill="rgba(255,255,255,.15)" stroke="rgba(255,255,255,.6)" strokeWidth="1.5"/>
      {[21,36,51,63].map((y, i) => <g key={i}><rect x="58" y={y} width="44" height="10" rx="2" fill="rgba(255,255,255,.2)"/><circle cx="94" cy={y+5} r="2.3" fill={i<2?"#69DB7C":"#FFD43B"}/></g>)}
    </svg>
  ),
  PRINTER: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <defs><linearGradient id="g14" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#6B7280"/><stop offset="100%" stopColor="#9CA3AF"/></linearGradient></defs>
      <rect width="160" height="90" fill="url(#g14)"/>
      <rect x="60" y="18" width="40" height="18" fill="rgba(255,255,255,.7)"/>
      <rect x="48" y="34" width="64" height="30" rx="4" fill="rgba(255,255,255,.92)"/>
      <circle cx="98" cy="44" r="2.6" fill="#6B7280"/>
      <rect x="60" y="58" width="40" height="20" fill="#fff"/>
    </svg>
  ),
  CLOUD_BACKUP: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <defs><linearGradient id="g15" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#15AABF"/><stop offset="100%" stopColor="#66D9E8"/></linearGradient></defs>
      <rect width="160" height="90" fill="url(#g15)"/>
      <path d="M55 60 C43 60 38 45 50 40 C51 27 72 22 80 32 C92 24 106 33 104 45 C116 46 116 60 104 60 Z" fill="rgba(255,255,255,.92)"/>
      <polyline points="70,58 80,48 90,58" stroke="#15AABF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <line x1="80" y1="48" x2="80" y2="68" stroke="#15AABF" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  ),
  IP_PHONE: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <defs><linearGradient id="g10" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#0CA678"/><stop offset="100%" stopColor="#63E6BE"/></linearGradient></defs>
      <rect width="160" height="90" fill="url(#g10)"/>
      <rect x="46" y="16" width="68" height="58" rx="8" fill="rgba(255,255,255,.92)"/>
      <rect x="54" y="24" width="52" height="15" rx="3" fill="#0CA678"/>
      {[0,1,2].map(r => (
        <g key={r}>
          {[0,1,2,3].map(c => <circle key={c} cx={60 + c*14} cy={48 + r*8} r="2.3" fill="#0CA678"/>)}
        </g>
      ))}
    </svg>
  ),
  PBX: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <defs><linearGradient id="g11" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#F08C00"/><stop offset="100%" stopColor="#FFC078"/></linearGradient></defs>
      <rect width="160" height="90" fill="url(#g11)"/>
      <rect x="28" y="28" width="104" height="34" rx="4" fill="rgba(255,255,255,.9)"/>
      {[0,1,2,3,4,5,6].map(i => <rect key={i} x={36 + i*13} y="36" width="8" height="14" rx="1.5" fill="#F08C00"/>)}
      <path d="M42 62 Q42 74 54 74" fill="none" stroke="rgba(255,255,255,.85)" strokeWidth="2.5"/>
      <path d="M96 62 Q96 74 108 74" fill="none" stroke="rgba(255,255,255,.85)" strokeWidth="2.5"/>
    </svg>
  ),
  SWITCH: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <defs><linearGradient id="g12" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#495057"/><stop offset="100%" stopColor="#868E96"/></linearGradient></defs>
      <rect width="160" height="90" fill="url(#g12)"/>
      <rect x="24" y="36" width="112" height="24" rx="4" fill="rgba(255,255,255,.15)" stroke="rgba(255,255,255,.55)" strokeWidth="1.5"/>
      {Array.from({length:10}).map((_,i) => <circle key={i} cx={34 + i*10.5} cy="48" r="2.2" fill={i%3===0 ? "#69DB7C":"rgba(255,255,255,.5)"}/>)}
    </svg>
  ),
  OTHER: (
    <svg viewBox="0 0 160 90" width="100%" height="100%">
      <rect width="160" height="90" fill="#f1f3f5"/>
      <circle cx="80" cy="45" r="20" fill="#F76707"/>
      <rect x="77" y="33" width="6" height="24" rx="3" fill="white"/>
      <rect x="68" y="42" width="24" height="6" rx="3" fill="white"/>
    </svg>
  ),
};

// ─────────────────────────────────────────────
//  CHAMP MOT DE PASSE + INDICATEUR DE FORCE
// ─────────────────────────────────────────────

function PasswordField({ value, onChange, label = "Mot de passe", required = false, loadingCurrent = false, showHistoryButton = false, historyOpen = false, onToggleHistory, showAccessLogButton = false, accessLogOpen = false, onOpenAccessLog }) {
  const [show, setShow] = useState(false);

  // Calcul de la force du mot de passe
  const getStrength = (pwd) => {
    if (!pwd) return null;
    let score = 0;
    if (pwd.length >= 8)           score++;
    if (pwd.length >= 12)          score++;
    if (/[A-Z]/.test(pwd))         score++;
    if (/[0-9]/.test(pwd))         score++;
    if (/[^A-Za-z0-9]/.test(pwd))  score++;
    const levels = [
      null,
      { label: "Très faible", color: "#fa5252" },
      { label: "Faible",      color: "#ff922b" },
      { label: "Moyen",       color: "#ffd43b" },
      { label: "Fort",        color: "#69db7c" },
      { label: "Très fort",   color: "#40c057" },
    ];
    return { score, ...levels[score] };
  };

  const strength = getStrength(value);

  return (
    <div className="mb-3">
      <label className="itm-label">
        {label} {required && <span style={{ color: "#fa5252" }}>*</span>}
      </label>
      <div className="input-group">
        <input
          type={show ? "text" : "password"}
          className="itm-input"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={loadingCurrent ? "Chargement du mot de passe actuel…" : "••••••••"}
          disabled={loadingCurrent}
        />
        <button type="button" className="itm-eye-btn" onClick={() => setShow(s => !s)} tabIndex={-1} disabled={loadingCurrent}>
          <i className={`bi ${loadingCurrent ? "bi-arrow-repeat spin" : show ? "bi-eye-slash" : "bi-eye"}`}/>
        </button>
        {showHistoryButton && (
          <button
            type="button"
            className={`itm-eye-btn${historyOpen ? " active" : ""}`}
            onClick={onToggleHistory}
            tabIndex={-1}
            disabled={loadingCurrent}
            title="Historique des mots de passe"
          >
            <i className="bi bi-clock-history"/>
          </button>
        )}
        {showAccessLogButton && (
          <button
            type="button"
            className={`itm-eye-btn${accessLogOpen ? " active" : ""}`}
            onClick={onOpenAccessLog}
            tabIndex={-1}
            disabled={loadingCurrent}
            title="Journal d'accès"
          >
            <i className="bi bi-journal-text"/>
          </button>
        )}
      </div>
      {/* Barre de force */}
      {strength && (
        <div className="mt-1">
          <div style={{ display: "flex", gap: 4, marginBottom: 3 }}>
            {[1,2,3,4,5].map(i => (
              <div key={i} style={{ height: 3, flex: 1, borderRadius: 4, background: i <= strength.score ? strength.color : "var(--navy-soft)", transition: "background .3s" }}/>
            ))}
          </div>
          <small style={{ color: strength.color, fontWeight: 600, fontSize: ".74rem" }}>{strength.label}</small>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  MODAL — HISTORIQUE DES MOTS DE PASSE
//  Superposée à la modale de modification (comme le sélecteur de type),
//  ouverte via l'icône horloge à côté du champ mot de passe. Tableau du
//  remplacement le plus récent au plus ancien, avec la personne à l'origine
//  de chaque changement, révélation et copie individuelles.
// ─────────────────────────────────────────────

function PasswordHistoryModal({ entries, loading, error, onClose }) {
  const [revealed, setRevealed] = useState({});

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  return (
    <div className="itm-overlay">
      <div className="itm-modal history-modal">
        <button className="itm-close-btn" onClick={onClose}><i className="bi bi-x-lg"/></button>
        <div className="history-modal-body">
          <h5 className="itm-modal-title mb-1"><i className="bi bi-clock-history me-2"/>Historique des mots de passe</h5>
          <p className="itm-modal-sub">Anciennes valeurs de ce mot de passe, de la plus récente à la plus ancienne.</p>

          {loading && (
            <div className="pwd-history-empty"><i className="bi bi-arrow-repeat spin me-1"/>Chargement…</div>
          )}

          {!loading && error && (
            <div className="pwd-history-empty pwd-history-error"><i className="bi bi-exclamation-triangle-fill me-1"/>{error}</div>
          )}

          {!loading && !error && (!entries || entries.length === 0) && (
            <div className="pwd-history-empty">Aucun ancien mot de passe — celui-ci n'a jamais été modifié.</div>
          )}

          {!loading && !error && entries && entries.length > 0 && (
            <div className="history-table-wrap">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Modifié par</th>
                    <th>Ancien mot de passe</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry, i) => {
                    const isRevealed = !!revealed[i];
                    return (
                      <tr key={i}>
                        <td className="history-td-date">{formatDate(entry.replacedAt)}</td>
                        <td className="history-td-user">{entry.changedBy || "—"}</td>
                        <td className="history-td-pwd">
                          {isRevealed ? entry.password : "•".repeat(Math.min(entry.password?.length || 8, 20))}
                        </td>
                        <td className="history-td-actions">
                          <button
                            type="button"
                            className="vw-icon-btn"
                            onClick={() => setRevealed(p => ({ ...p, [i]: !p[i] }))}
                            title={isRevealed ? "Masquer" : "Afficher"}
                          >
                            <i className={`bi ${isRevealed ? "bi-eye-slash" : "bi-eye"}`}/>
                          </button>
                          <button
                            type="button"
                            className="vw-icon-btn"
                            onClick={() => entry.password && navigator.clipboard.writeText(entry.password)}
                            title="Copier"
                          >
                            <i className="bi bi-copy"/>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  MODAL — JOURNAL D'ACCÈS
//  Qui a consulté ou copié un secret (mot de passe, champ personnalisé
//  masqué, code TOTP) et quand — distinct de l'historique des mots de passe
//  (qui trace les remplacements, pas les consultations). Superposée à la
//  modale de modification, comme le sélecteur de type et l'historique.
// ─────────────────────────────────────────────

const ACCESS_TARGET_LABELS = { PASSWORD: "Mot de passe", CUSTOM_FIELD: "Champ personnalisé", TOTP: "Code 2FA" };
const ACCESS_ACTION_LABELS = { VIEW: "Consulté", COPY: "Copié", CHANGE: "Modifié" };

function AccessLogModal({ entries, loading, error, onClose }) {
  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  const formatTarget = (entry) => {
    const label = ACCESS_TARGET_LABELS[entry.targetType] || entry.targetType;
    return entry.targetType === "CUSTOM_FIELD" && entry.fieldName ? `${label} — ${entry.fieldName}` : label;
  };

  return (
    <div className="itm-overlay">
      <div className="itm-modal history-modal">
        <button className="itm-close-btn" onClick={onClose}><i className="bi bi-x-lg"/></button>
        <div className="history-modal-body">
          <h5 className="itm-modal-title mb-1"><i className="bi bi-journal-text me-2"/>Journal d'accès</h5>
          <p className="itm-modal-sub">Qui a consulté ou copié un secret de cette credential, et quand.</p>

          {loading && (
            <div className="pwd-history-empty"><i className="bi bi-arrow-repeat spin me-1"/>Chargement…</div>
          )}

          {!loading && error && (
            <div className="pwd-history-empty pwd-history-error"><i className="bi bi-exclamation-triangle-fill me-1"/>{error}</div>
          )}

          {!loading && !error && (!entries || entries.length === 0) && (
            <div className="pwd-history-empty">Aucun accès enregistré pour l'instant.</div>
          )}

          {!loading && !error && entries && entries.length > 0 && (
            <div className="history-table-wrap">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Utilisateur</th>
                    <th>Action</th>
                    <th>Cible</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry, i) => (
                    <tr key={i}>
                      <td className="history-td-date">{formatDate(entry.accessedAt)}</td>
                      <td className="history-td-user">{entry.userName || "—"}</td>
                      <td>{ACCESS_ACTION_LABELS[entry.action] || entry.action}</td>
                      <td>{formatTarget(entry)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  GÉNÉRATEUR DE MOT DE PASSE INLINE
//  generateFn(length) : fonction de génération (client ou admin selon le contexte)
// ─────────────────────────────────────────────

function InlinePasswordGenerator({ generateFn, onGenerated }) {
  const [length,  setLength]  = useState(16);
  const [loading, setLoading] = useState(false);
  const [show,    setShow]    = useState(false);
  const [last,    setLast]    = useState("");

  const generate = async () => {
    setLoading(true);
    try {
      const pwd = await generateFn(length);
      setLast(pwd);
      setShow(true);
      onGenerated(pwd);
    } catch (e) {
      alert("Erreur : " + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pwd-gen">
      <div className="pwd-gen-row">
        <span className="pwd-gen-label">Générer&nbsp;: {length} caractères</span>
        <input
          type="range" min={8} max={32} value={length}
          className="pwd-gen-slider"
          onChange={e => setLength(parseInt(e.target.value))}
        />
        <button type="button" className="pwd-gen-btn" onClick={generate} disabled={loading}>
          {loading ? <i className="bi bi-arrow-repeat spin"/> : <><i className="bi bi-magic me-1"/>Générer</>}
        </button>
      </div>
      {last && (
        <div className="pwd-gen-preview">
          <span>{show ? last : "•".repeat(Math.min(last.length, 24))}</span>
          <div style={{ display: "flex", gap: 2 }}>
            <button type="button" className="vw-icon-btn" onClick={() => setShow(s => !s)} title={show ? "Masquer" : "Afficher"}>
              <i className={`bi ${show ? "bi-eye-slash" : "bi-eye"}`}/>
            </button>
            <button type="button" className="vw-icon-btn" onClick={() => navigator.clipboard.writeText(last)} title="Copier">
              <i className="bi bi-copy"/>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  FORMULAIRE — champs communs + champs par type
//  Aligné sur les champs de Credential.java :
//  name, username, encryptedPassword, url, host, port, notes, type
// ─────────────────────────────────────────────

function CredentialForm({ typeId, data, onChange, isEdit, folders = [], isAdminMode = false, companyId = null, credentialId = null, loadingCurrentPassword = false, showHistoryButton = false, historyOpen = false, onToggleHistory, accessLogOpen = false, onOpenAccessLog }) {
  // URL et Adresse IP/Hostname sont désormais affichés pour tous les types de
  // credential, sans distinction — un site web peut avoir une IP dédiée, un
  // serveur peut avoir une URL d'admin, etc.
  // Révélation individuelle des champs personnalisés de type "Mot de passe"
  const [revealedCF, setRevealedCF] = useState({});

  // Journal d'accès — best-effort, ne bloque jamais l'action réelle (copie,
  // révélation) : on avale toute erreur de journalisation en silence.
  const logFieldAccess = (action, fieldName) => {
    if (!credentialId) return;
    const fn = isAdminMode ? adminLogCredentialAccess : logCredentialAccess;
    fn(credentialId, { targetType: "CUSTOM_FIELD", action, fieldName }).catch(() => {});
  };

  return (
    <>
      {/* Nom — obligatoire */}
      <div className="mb-3">
        <label className="itm-label">Nom <span style={{ color: "#fa5252" }}>*</span></label>
        <input
          className="itm-input"
          value={data.name || ""}
          onChange={e => onChange("name", e.target.value)}
          placeholder="Ex : Serveur prod, Gmail, GitHub…"
        />
      </div>

      {/* Dossier — uniquement côté admin (gestion des credentials d'une entreprise) */}
      {isAdminMode && folders.length > 0 && (
        <div className="mb-3">
          <label className="itm-label">Dossier</label>
          <select
            className="itm-input"
            value={data.folderId || ""}
            onChange={e => onChange("folderId", e.target.value)}
          >
            <option value="">— Sans dossier —</option>
            {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
      )}

      {/* URL */}
      <div className="mb-3">
        <label className="itm-label">URL</label>
        <input
          className="itm-input"
          value={data.url || ""}
          onChange={e => onChange("url", e.target.value)}
          placeholder="https://exemple.com"
        />
      </div>

      {/* Host + Port */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 100px", gap: 10 }} className="mb-3">
        <div>
          <label className="itm-label">Adresse IP / Hostname</label>
          <input
            className="itm-input"
            value={data.host || ""}
            onChange={e => onChange("host", e.target.value)}
            placeholder="192.168.1.10"
          />
        </div>
        <div>
          <label className="itm-label">Port</label>
          <input
            className="itm-input"
            value={data.port || ""}
            onChange={e => onChange("port", e.target.value)}
            placeholder={typeId === "SSH" ? "22" : typeId === "DATABASE" ? "5432" : "3389"}
          />
        </div>
      </div>

      {/* Nom d'utilisateur */}
      <div className="mb-3">
        <label className="itm-label">Nom d'utilisateur</label>
        <input
          className="itm-input"
          value={data.username || ""}
          onChange={e => onChange("username", e.target.value)}
          placeholder="john.doe, root, admin…"
        />
      </div>

      {/* Mot de passe — en édition, le mot de passe actuel est chargé automatiquement
          (déchiffré depuis le backend) et affiché masqué ; l'œil le révèle/masque.
          Modifier sa valeur avant d'enregistrer revient à le changer. */}
      <PasswordField
        value={data.encryptedPassword || ""}
        onChange={v => onChange("encryptedPassword", v)}
        required={!isEdit}
        label={isEdit ? "Mot de passe (modifiez-le pour le changer)" : "Mot de passe"}
        loadingCurrent={isEdit && loadingCurrentPassword}
        showHistoryButton={showHistoryButton}
        historyOpen={historyOpen}
        onToggleHistory={onToggleHistory}
        showAccessLogButton={showHistoryButton}
        accessLogOpen={accessLogOpen}
        onOpenAccessLog={onOpenAccessLog}
      />

      {/* Générateur de mot de passe */}
      <InlinePasswordGenerator
        generateFn={(len) => (isAdminMode ? adminGeneratePassword(companyId, len) : generatePassword(len))}
        onGenerated={(pwd) => onChange("encryptedPassword", pwd)}
      />

      {/* Champs personnalisés — façon PasswordBoss : nom + valeur + type */}
      <div className="mb-3">
        <label className="itm-label">Champs personnalisés</label>
        {(data.customFields || []).map((cf, i) => {
          const cfType   = cf.type || "TEXT";
          const isPwd    = cfType === "PASSWORD";
          const revealed = !!revealedCF[i];
          return (
            <div key={i} className="cf-row">
              <div className="cf-row-top">
                <input
                  className="itm-input"
                  value={cf.name}
                  onChange={e => {
                    const next = [...data.customFields];
                    next[i] = { ...next[i], name: e.target.value };
                    onChange("customFields", next);
                  }}
                  placeholder="Nom (ex : Code PIN)"
                />
                <select
                  className="itm-input cf-type-select"
                  value={cfType}
                  title="Type de champ"
                  onChange={e => {
                    const next = [...data.customFields];
                    next[i] = { ...next[i], type: e.target.value };
                    onChange("customFields", next);
                  }}
                >
                  {CUSTOM_FIELD_TYPES.map(t => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => onChange("customFields", data.customFields.filter((_, idx) => idx !== i))}
                  title="Supprimer ce champ"
                  className="cf-del-btn"
                >
                  <i className="bi bi-x-lg" />
                </button>
              </div>
              {cfType === "CHECKBOX" ? (
                <label className="cf-checkbox-wrap">
                  <input
                    type="checkbox"
                    checked={!!cf.value}
                    onChange={e => {
                      const next = [...data.customFields];
                      next[i] = { ...next[i], value: e.target.checked };
                      onChange("customFields", next);
                    }}
                  />
                  <span className="cf-checkbox-slider" />
                  <span className="cf-checkbox-state">{cf.value ? "Activé" : "Désactivé"}</span>
                </label>
              ) : (
                <div className="cf-value-wrap">
                  <input
                    className="itm-input"
                    type={isPwd && !revealed ? "password" : cfType === "DATE" ? "date" : "text"}
                    value={cf.value}
                    onChange={e => {
                      const next = [...data.customFields];
                      next[i] = { ...next[i], value: e.target.value };
                      onChange("customFields", next);
                    }}
                    placeholder="Valeur"
                    style={{ paddingRight: isPwd ? 62 : 34 }}
                  />
                  <div className="cf-value-icons">
                    {isPwd && (
                      <button
                        type="button"
                        onClick={() => {
                          if (!revealed) logFieldAccess("VIEW", cf.name);
                          setRevealedCF(p => ({ ...p, [i]: !p[i] }));
                        }}
                        title={revealed ? "Masquer" : "Révéler"}
                      >
                        <i className={`bi ${revealed ? "bi-eye-slash" : "bi-eye"}`}/>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        if (!cf.value) return;
                        if (isPwd) logFieldAccess("COPY", cf.name);
                        navigator.clipboard.writeText(cf.value);
                      }}
                      title="Copier"
                    >
                      <i className="bi bi-copy"/>
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        <button
          type="button"
          onClick={() => onChange("customFields", [...(data.customFields || []), { name: "", value: "", type: "TEXT" }])}
          className="cf-add-btn"
        >
          <i className="bi bi-plus-circle" />Ajouter un champ personnalisé
        </button>
      </div>

      {/* Notes */}
      <div className="mb-3">
        <label className="itm-label">Notes</label>
        <textarea
          className="itm-input"
          rows={3}
          value={data.notes || ""}
          onChange={e => onChange("notes", e.target.value)}
          placeholder="Informations supplémentaires…"
          style={{ resize: "vertical" }}
        />
      </div>
    </>
  );
}

// ─────────────────────────────────────────────
//  MODAL — SÉLECTION DU TYPE (étape 1)
// ─────────────────────────────────────────────

function TypePickerModal({ onSelect, onClose }) {
  return (
    <div className="itm-overlay">
      <div className="itm-modal picker-modal">
        <button className="itm-close-btn" onClick={onClose}><i className="bi bi-x-lg"/></button>
        <h5 className="itm-modal-title">Ajouter un mot de passe</h5>
        <p className="itm-modal-sub">Quel type de credential voulez-vous ajouter ?</p>
        <div className="type-grid">
          {TYPES.map(t => (
            <button key={t.id} className="type-tile" onClick={() => onSelect(t)}>
              <div className="type-banner">{SVG[t.id]}</div>
              <span className="type-label">{t.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  BANNIÈRE DU FORMULAIRE — vrai logo si détecté, sinon illustration par type
// ─────────────────────────────────────────────

/** Affiche le vrai logo (favicon) de la marque détectée depuis le nom/l'URL
 *  en cours de saisie, à la place de l'illustration générique — se met à
 *  jour en direct pendant que l'utilisateur tape. Retombe automatiquement
 *  sur l'illustration si aucune marque n'est reconnue ou si l'image échoue. */
function TypeBanner({ type, data }) {
  const [failedDomain, setFailedDomain] = useState(null);
  const eligible = BRAND_AWARE_TYPES.has(type.id);
  const domain = eligible ? resolveBrandDomain(data.name, data.url, data.host) : null;

  if (!domain || domain === failedDomain) {
    return SVG[type.id];
  }

  return (
    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center",
                  background: `linear-gradient(135deg, ${type.bg}22, ${type.bg}0D)` }}>
      <div style={{ width: 64, height: 64, borderRadius: 16, background: "#fff",
                    boxShadow: "0 6px 20px rgba(0,0,0,.12)", display: "flex",
                    alignItems: "center", justifyContent: "center" }}>
        <img
          key={domain}
          src={faviconSrc(domain, 128)}
          alt=""
          onError={() => setFailedDomain(domain)}
          style={{ width: 36, height: 36, objectFit: "contain" }}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  MODAL — FORMULAIRE (étape 2)
// ─────────────────────────────────────────────

// defaultScope : scope transmis par la page parente ("PERSONAL" depuis les pages client,
// "COMPANY" depuis les pages admin)
// companyId/folders/isAdminMode : présents uniquement côté admin (gestion des credentials
// d'une entreprise cliente) — voir CredentialModal ci-dessous
function FormModal({ type: initialType, editData, defaultScope = "PERSONAL", companyId = null, folders = [], isAdminMode = false, onClose, onSave }) {
  const isEdit = !!editData;

  // Le type est gardé en état local (et non piloté par le parent) pour pouvoir
  // le changer sans perdre les données déjà saisies dans le formulaire —
  // notamment en modification, où repasser par le sélecteur de type effacerait
  // sinon le mot de passe, les notes, etc.
  const [type, setType] = useState(initialType);
  const [showTypePicker, setShowTypePicker] = useState(false);

  // Pré-remplissage en mode édition (sans le mot de passe chiffré)
  // customFields est stocké en JSON (string) côté backend — on le parse en tableau ici
  const [data, setData] = useState(() => {
    if (isEdit) {
      let parsedCustomFields = [];
      try {
        parsedCustomFields = editData.customFields ? JSON.parse(editData.customFields) : [];
      } catch { parsedCustomFields = []; }
      return { ...editData, encryptedPassword: "", customFields: parsedCustomFields, folderId: editData?.folder?.id || "" };
    }
    return { name: "", username: "", encryptedPassword: "", url: "", host: "", port: "", notes: "", customFields: [], folderId: "" };
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [loadingCurrentPassword, setLoadingCurrentPassword] = useState(false);

  // Historique des anciens mots de passe — chargé à la demande (pas à l'ouverture
  // de la modale) pour éviter un aller-retour serveur inutile la plupart du temps
  const [historyOpen,    setHistoryOpen]    = useState(false);
  const [historyLoaded,  setHistoryLoaded]  = useState(false);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError,   setHistoryError]   = useState("");

  // Journal d'accès — même logique de chargement à la demande que l'historique
  const [accessLogOpen,    setAccessLogOpen]    = useState(false);
  const [accessLogLoaded,  setAccessLogLoaded]  = useState(false);
  const [accessLogEntries, setAccessLogEntries] = useState([]);
  const [accessLogLoading, setAccessLogLoading] = useState(false);
  const [accessLogError,   setAccessLogError]   = useState("");

  const handleChange = (key, val) => setData(prev => ({ ...prev, [key]: val }));

  // Charge le mot de passe actuellement enregistré — déchiffré côté backend.
  // PERSONAL → route client (CREDENTIAL_VIEW_PERSONAL) ; COMPANY (admin) → route admin (SUPER_ADMIN)
  const handleDownloadCurrentPassword = async () => {
    if (!editData?.id) return;
    const fetchFn = isAdminMode ? adminGetPassword : getCredentialPassword;
    const res = await fetchFn(editData.id);
    const pwd = res?.data ?? res;
    handleChange("encryptedPassword", pwd || "");
  };

  // En édition, on charge automatiquement le mot de passe actuel dès l'ouverture
  // de la modale — plus besoin de cliquer pour le récupérer, il est prêt derrière
  // l'œil (masqué par défaut) comme demandé.
  useEffect(() => {
    if (!isEdit || !editData?.id) return;
    setLoadingCurrentPassword(true);
    handleDownloadCurrentPassword().finally(() => setLoadingCurrentPassword(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ouvre la modale d'historique — charge la liste une seule fois (au premier
  // clic), puis se contente de la ré-afficher ensuite sans nouvel appel réseau.
  const handleOpenHistory = async () => {
    if (!editData?.id) return;
    setHistoryOpen(true);
    if (historyLoaded) return;

    setHistoryLoading(true);
    setHistoryError("");
    try {
      const fetchFn = isAdminMode ? adminGetPasswordHistory : getCredentialPasswordHistory;
      const res = await fetchFn(editData.id);
      setHistoryEntries(Array.isArray(res) ? res : []);
      setHistoryLoaded(true);
    } catch (e) {
      setHistoryError(e.message || "Impossible de charger l'historique.");
    } finally {
      setHistoryLoading(false);
    }
  };

  // Ouvre la modale du journal d'accès — même principe : chargée une seule
  // fois, puis simplement ré-affichée.
  const handleOpenAccessLog = async () => {
    if (!editData?.id) return;
    setAccessLogOpen(true);
    if (accessLogLoaded) return;

    setAccessLogLoading(true);
    setAccessLogError("");
    try {
      const fetchFn = isAdminMode ? adminGetAccessLog : getCredentialAccessLog;
      const res = await fetchFn(editData.id, 0, 50);
      setAccessLogEntries(Array.isArray(res?.content) ? res.content : []);
      setAccessLogLoaded(true);
    } catch (e) {
      setAccessLogError(e.message || "Impossible de charger le journal d'accès.");
    } finally {
      setAccessLogLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!data.name?.trim()) {
      setError("Le nom est obligatoire.");
      return;
    }
// Mot de passe optionnel — pas de validation obligatoire

    setLoading(true);
    setError("");

    try {
      // Payload — aligné sur Credential.java
      const payload = {
        name:     data.name,
        username: data.username  || null,
        url:      data.url       || null,
        host:     data.host      || null,
        port:     data.port      ? parseInt(data.port) : null,
        notes:    data.notes     || null,
        type:     type.id,
        // Scope : "PERSONAL" depuis les pages client, "COMPANY" depuis les pages admin
        scope:    defaultScope,
        // Champs personnalisés — sérialisés en JSON, on ne garde que ceux avec un nom rempli
        customFields: JSON.stringify((data.customFields || []).filter(cf => cf.name?.trim())),
        // Mot de passe : envoyé uniquement s'il est rempli
        ...(data.encryptedPassword?.trim()
          ? { encryptedPassword: data.encryptedPassword }
          : {}),
        // Entreprise + dossier — uniquement en mode admin
        ...(isAdminMode
          ? { company: { id: companyId }, folder: data.folderId ? { id: data.folderId } : null }
          : {}),
      };

      if (isEdit) {
        await (isAdminMode ? adminUpdateCredential(editData.id, payload) : updateCredential(editData.id, payload));
      } else {
        await (isAdminMode ? adminCreateCredential(payload) : createCredential(payload));
      }

      onSave(); // recharge la liste dans la page parente

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="itm-overlay">
      <div className="itm-modal form-modal">
        <button className="itm-close-btn" onClick={onClose}><i className="bi bi-x-lg"/></button>

        {/* Bannière — vrai logo de la marque si détecté (nom ou URL), sinon illustration générique */}
        <div className="form-banner">
          <TypeBanner type={type} data={data}/>
          <button type="button" className="itm-type-change-btn" onClick={() => setShowTypePicker(true)}>
            <i className="bi bi-arrow-repeat me-1"/>Changer le type
          </button>
        </div>

        {/* Erreur — affichée en haut, hors du scroll */}
        {error && (
          <div style={{ padding:"10px 18px", background:"rgba(239,68,68,.08)", borderBottom:"1px solid rgba(239,68,68,.2)", display:"flex", alignItems:"center", gap:8, fontSize:13, color:"#E63329", flexShrink:0 }}>
            <i className="bi bi-exclamation-triangle-fill" style={{ flexShrink:0 }}/>
            {error}
          </div>
        )}

        {/* Corps */}
        <div className="form-body">
          <h5 className="itm-modal-title mb-1">
            {isEdit ? "Modifier" : "Ajouter"} — {type.label}
          </h5>

          <CredentialForm
            typeId={type.id}
            data={data}
            onChange={handleChange}
            isEdit={isEdit}
            folders={folders}
            isAdminMode={isAdminMode}
            companyId={companyId}
            credentialId={editData?.id}
            loadingCurrentPassword={loadingCurrentPassword}
            showHistoryButton={isEdit && !!editData?.id}
            historyOpen={historyOpen}
            onToggleHistory={handleOpenHistory}
            accessLogOpen={accessLogOpen}
            onOpenAccessLog={handleOpenAccessLog}
          />

          {/* ── 2FA / TOTP — uniquement en mode édition ── */}
          {isEdit && editData?.id && (
            <div className="mb-3">
              <label className="itm-label">Double authentification (2FA)</label>
              <TotpWidget
                credentialId={editData.id}
                hasTotpSecret={!!editData.hasTotpSecret}
                onUpdate={() => {}}
                readOnly={false}
                admin={isAdminMode}
              />
            </div>
          )}
        </div>

        {/* Footer épinglé en bas — hors du scroll */}
        <div className="form-actions">
          <button type="button" className="itm-btn-ghost" onClick={onClose}>
            Annuler
          </button>
          <button
            type="button"
            className="itm-btn-primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading
              ? <><i className="bi bi-arrow-repeat spin me-1"/>Enregistrement…</>
              : <><i className="bi bi-check-lg me-1"/>{isEdit ? "Mettre à jour" : "Enregistrer"}</>
            }
          </button>
        </div>
      </div>

      {/* Sélecteur de type — superposé, ne réinitialise pas les données déjà saisies */}
      {showTypePicker && (
        <TypePickerModal
          onSelect={t => { setType(t); setShowTypePicker(false); }}
          onClose={() => setShowTypePicker(false)}
        />
      )}

      {/* Historique des mots de passe — superposé, comme le sélecteur de type */}
      {historyOpen && (
        <PasswordHistoryModal
          entries={historyEntries}
          loading={historyLoading}
          error={historyError}
          onClose={() => setHistoryOpen(false)}
        />
      )}

      {/* Journal d'accès — superposé, même principe */}
      {accessLogOpen && (
        <AccessLogModal
          entries={accessLogEntries}
          loading={accessLogLoading}
          error={accessLogError}
          onClose={() => setAccessLogOpen(false)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  COMPOSANT EXPORTÉ
//
//  Props :
//   - editData      : credential à modifier (null = création)
//   - defaultScope  : "PERSONAL" | "COMPANY" — transmis au payload lors de la création
//   - companyId     : UUID de l'entreprise ciblée — présent uniquement côté admin
//                      (PasswordsPage). Sa présence active le mode admin : payload
//                      enrichi (company/folder) et endpoints /credentials/admin/*.
//   - folders       : dossiers de l'entreprise — présent uniquement côté admin
//   - onExternalSave  : callback après sauvegarde réussie
//   - onExternalClose : callback pour fermer la modale
// ─────────────────────────────────────────────

export default function CredentialModal({ editData, defaultScope = "PERSONAL", companyId = null, folders = [], onExternalSave, onExternalClose }) {
  // En mode édition → on résout directement le type depuis la credential
  const [selectedType, setSelectedType] = useState(
    editData
      ? TYPES.find(t => t.id === (editData.type || "OTHER").toUpperCase()) || TYPES[TYPES.length - 1]
      : null
  );

  // Mode admin : dès qu'un companyId est fourni (gestion des credentials d'une
  // entreprise cliente) — sinon mode "personnel" identique à avant (PERSONAL)
  const isAdminMode = !!companyId;

  return (
    <>
      {/* Étape 1 — choisir le type (création uniquement) */}
      {!selectedType && (
        <TypePickerModal
          onSelect={t => setSelectedType(t)}
          onClose={onExternalClose}
        />
      )}

      {/* Étape 2 — remplir le formulaire */}
      {selectedType && (
        <FormModal
          type={selectedType}
          editData={editData}
          defaultScope={defaultScope}
          companyId={companyId}
          folders={folders}
          isAdminMode={isAdminMode}
          onClose={() => editData ? onExternalClose?.() : setSelectedType(null)}
          onSave={onExternalSave}
        />
      )}

      <style>{CSS}</style>
    </>
  );
}

// ─────────────────────────────────────────────
//  STYLES
// ─────────────────────────────────────────────
const CSS = `
.itm-overlay{position:fixed;inset:0;z-index:1055;background:rgba(26,26,26,.6);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:16px;animation:itmFade .15s ease}
@keyframes itmFade{from{opacity:0}to{opacity:1}}
.itm-modal{background:var(--card-bg);border:1px solid var(--border);border-radius:16px;box-shadow:0 32px 100px rgba(0,0,0,.25);position:relative;width:100%;animation:itmSlide .22s cubic-bezier(.34,1.56,.64,1);max-height:92vh}
@keyframes itmSlide{from{transform:translateY(28px);opacity:0}to{transform:none;opacity:1}}
.picker-modal{max-width:500px;padding:28px 24px 24px;overflow-y:auto}
.form-modal{max-width:490px;display:flex;flex-direction:column;overflow:hidden}
.form-banner{width:100%;aspect-ratio:16/6;flex-shrink:0;overflow:hidden;border-radius:16px 16px 0 0;position:relative}
.itm-type-change-btn{position:absolute;left:14px;bottom:12px;z-index:2;display:flex;align-items:center;background:rgba(0,0,0,.4);border:none;cursor:pointer;color:#fff;font-size:12px;font-weight:600;padding:6px 12px;border-radius:20px;transition:background .15s}
.itm-type-change-btn:hover{background:rgba(0,0,0,.6)}
.form-banner svg{display:block;width:100%;height:100%}
.form-body{padding:22px 24px 12px;overflow-y:auto;flex:1}
.itm-close-btn{position:absolute;top:12px;right:14px;z-index:2;background:rgba(0,0,0,.35);border:none;cursor:pointer;color:#fff;font-size:13px;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;transition:all .15s}
.itm-close-btn:hover{background:#E63329;color:#fff}
.itm-modal-title{font-size:1.1rem;font-weight:700;color:var(--text-1);margin-bottom:4px}
.itm-modal-sub{color:var(--text-2);font-size:.88rem;margin-bottom:20px}
.type-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:10px}
.type-tile{display:flex;flex-direction:column;align-items:stretch;border:1.5px solid var(--border);border-radius:10px;background:var(--navy-mid);cursor:pointer;padding:0;overflow:hidden;transition:all .18s}
.type-tile:hover{border-color:#E63329;background:rgba(230,51,41,.06);transform:translateY(-2px);box-shadow:0 6px 20px rgba(230,51,41,.15)}
.type-banner{width:100%;aspect-ratio:16/9;overflow:hidden;display:block;line-height:0}
.type-banner svg{display:block;width:100%;height:100%}
.type-label{display:block;padding:7px 5px 8px;font-size:.72rem;font-weight:500;color:var(--text-2);line-height:1.3;text-align:center}
.itm-label{display:block;font-size:.78rem;font-weight:600;color:var(--text-2);margin-bottom:5px;letter-spacing:.03em;text-transform:uppercase}
.itm-input{display:block;width:100%;background:var(--navy-mid);border:1.5px solid var(--border-md);border-radius:8px;color:var(--text-1);padding:9px 12px;font-size:.88rem;transition:border-color .18s,box-shadow .18s;outline:none}
.itm-input:focus{border-color:#E63329;box-shadow:0 0 0 3px rgba(230,51,41,.15);background:var(--card-bg)}
.itm-input::placeholder{color:var(--text-3)}
textarea.itm-input{resize:vertical}
.input-group{display:flex}
.input-group .itm-input{border-radius:8px 0 0 8px;flex:1}
.itm-eye-btn{background:var(--navy-mid);border:1.5px solid var(--border-md);border-left:none;color:var(--text-3);padding:0 12px;cursor:pointer;transition:color .15s,background .15s;flex-shrink:0}
.itm-eye-btn:last-child{border-radius:0 8px 8px 0}
.itm-eye-btn:hover{color:var(--text-1)}
.itm-eye-btn.active{color:#E63329;background:var(--card-bg)}
.history-modal{max-width:640px;display:flex;flex-direction:column;overflow:hidden}
.history-modal-body{padding:26px 24px 22px;overflow-y:auto}
.history-table-wrap{overflow-x:auto;border:1px solid var(--border);border-radius:10px}
.history-table{width:100%;border-collapse:collapse;font-size:.86rem}
.history-table thead th{text-align:left;font-size:.7rem;font-weight:700;text-transform:uppercase;letter-spacing:.03em;color:var(--text-3);background:var(--navy-mid);padding:10px 14px;border-bottom:1px solid var(--border);white-space:nowrap}
.history-table tbody td{padding:10px 14px;border-bottom:1px solid var(--border);color:var(--text-1);vertical-align:middle}
.history-table tbody tr:last-child td{border-bottom:none}
.history-table tbody tr:hover{background:var(--navy-mid)}
.history-td-date{white-space:nowrap;color:var(--text-2);font-size:.82rem}
.history-td-user{white-space:nowrap;font-weight:600}
.history-td-pwd{font-family:monospace;word-break:break-all}
.history-td-actions{white-space:nowrap;text-align:right}
.pwd-history-empty{padding:24px 12px;text-align:center;font-size:.86rem;color:var(--text-3)}
.pwd-history-error{color:#E63329}
.form-actions{display:flex;justify-content:flex-end;gap:10px;padding:14px 24px;border-top:1px solid var(--border);background:var(--card-bg);border-radius:0 0 16px 16px;flex-shrink:0}
.itm-btn-ghost{background:var(--card-bg);border:1.5px solid var(--border-md);border-radius:8px;color:var(--text-2);padding:8px 20px;font-size:.88rem;cursor:pointer;transition:all .15s}
.itm-btn-ghost:hover{background:var(--navy-mid);color:var(--text-1)}
.itm-btn-primary{background:#E63329;border:none;border-radius:8px;color:#fff;padding:8px 22px;font-size:.88rem;font-weight:600;cursor:pointer;transition:filter .18s;display:inline-flex;align-items:center}
.itm-btn-primary:hover{filter:brightness(1.15)}
.itm-btn-primary:disabled{opacity:.6;cursor:not-allowed}
.cf-row{border:1px solid var(--border);border-radius:10px;padding:10px;margin-bottom:8px;background:var(--navy-mid)}
.cf-row-top{display:grid;grid-template-columns:1fr 118px auto;gap:8px;margin-bottom:8px;align-items:center}
.cf-type-select{cursor:pointer;-webkit-appearance:menulist;appearance:menulist}
.cf-del-btn{background:var(--card-bg);border:1px solid var(--border-md);border-radius:8px;width:38px;height:38px;cursor:pointer;color:var(--text-3);flex-shrink:0;transition:all .15s}
.cf-del-btn:hover{color:#E63329;border-color:#E63329}
.cf-value-wrap{position:relative}
.cf-value-icons{position:absolute;right:5px;top:50%;transform:translateY(-50%);display:flex;gap:1px}
.cf-value-icons button{background:none;border:none;cursor:pointer;color:var(--text-3);padding:6px;font-size:13px;display:flex;align-items:center;justify-content:center;transition:color .15s}
.cf-value-icons button:hover{color:var(--text-1)}
.cf-add-btn{display:flex;align-items:center;gap:6px;background:none;border:1px dashed rgba(0,0,0,.15);border-radius:8px;padding:8px 12px;cursor:pointer;color:var(--text-2);font-size:13px;width:100%;justify-content:center}
.cf-checkbox-wrap{display:flex;align-items:center;gap:10px;cursor:pointer;padding:2px 0}
.cf-checkbox-wrap input{position:absolute;opacity:0;width:0;height:0}
.cf-checkbox-slider{position:relative;flex-shrink:0;width:38px;height:22px;border-radius:99px;background:rgba(0,0,0,.15);transition:background .18s}
.cf-checkbox-slider::before{content:"";position:absolute;top:2px;left:2px;width:18px;height:18px;border-radius:50%;background:var(--card-bg);box-shadow:0 1px 3px rgba(0,0,0,.25);transition:transform .18s}
.cf-checkbox-wrap input:checked + .cf-checkbox-slider{background:#E63329}
.cf-checkbox-wrap input:checked + .cf-checkbox-slider::before{transform:translateX(16px)}
.cf-checkbox-state{font-size:.82rem;color:var(--text-2)}
.itm-eye-btn-mid{background:var(--navy-mid);border:1.5px solid var(--border-md);border-left:none;color:var(--text-3);padding:0 12px;cursor:pointer;transition:color .15s;flex-shrink:0}
.itm-eye-btn-mid:hover{color:var(--text-1)}
.pwd-gen{margin-top:8px;padding:10px 12px;border:1px solid var(--border);border-radius:8px;background:var(--navy-mid)}
.pwd-gen-row{display:flex;align-items:center;gap:10px}
.pwd-gen-label{font-size:.76rem;color:var(--text-3);white-space:nowrap;flex-shrink:0}
.pwd-gen-slider{flex:1;accent-color:#E63329}
.pwd-gen-btn{display:flex;align-items:center;background:rgba(230,51,41,.1);border:1.5px solid rgba(230,51,41,.3);border-radius:7px;color:#E63329;font-size:.8rem;font-weight:600;padding:6px 12px;cursor:pointer;white-space:nowrap;transition:all .15s}
.pwd-gen-btn:hover{background:#E63329;color:#fff}
.pwd-gen-btn:disabled{opacity:.6;cursor:not-allowed}
.pwd-gen-preview{margin-top:8px;display:flex;align-items:center;justify-content:space-between;gap:8px;background:var(--card-bg);border:1px solid var(--border);border-radius:7px;padding:7px 10px;font-family:monospace;font-size:.84rem;color:var(--text-1);word-break:break-all}
.vw-icon-btn{background:none;border:none;cursor:pointer;color:var(--text-3);padding:4px;font-size:13px;display:flex;align-items:center;justify-content:center;transition:color .15s}
.vw-icon-btn:hover{color:var(--text-1)}
.mb-3{margin-bottom:14px}
`;